/**
 * PublicFieldValue — type-aware field value renderer for public pages.
 *
 * All public view type components (card, list, table, timeline) delegate
 * individual field rendering to this component for consistency.
 *
 * A `datetime` column is TIMESTAMP (no zone) holding UTC. `new Date()` on one
 * reads it as the VIEWER's wall clock, which is wrong by their whole offset —
 * enough to move an early-morning event onto the previous day. parseInstant
 * reads it as the UTC it is, and `timeZone` (the owning record's own, via
 * zoneOf) decides which wall clock to show it on.
 */

import React from 'react';
import envConfig from '../../config/envConfig';
import {
  formatInstantTime, localeDateIn, parseInstant, toDisplayString,
} from '../../utils/utils';

function resolveFileUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${envConfig.fileServerUrl}/public/${value}`;
}

export default function PublicFieldValue({ field, value, badgeColor, currencySymbol, timeZone }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  switch (field.data_type) {
    case 'image':
      return (
        <img
          src={resolveFileUrl(value)}
          alt={field.display_name}
          className="img-fluid rounded"
          style={{ maxHeight: 200, objectFit: 'cover' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );

    case 'file':
      return (
        <a
          href={resolveFileUrl(value)}
          target="_blank"
          rel="noreferrer"
          className="btn btn-sm btn-outline-secondary"
        >
          Download
        </a>
      );

    case 'boolean':
      return value
        ? <span className="badge bg-success">Yes</span>
        : <span className="badge bg-secondary">No</span>;

    case 'enum':
      return (
        <span className={`badge bg-${badgeColor || 'primary'}`}>{value}</span>
      );

    // A day is a day everywhere, so a date-only value is NOT zoned — reading it
    // in the record's zone would walk it across midnight for no gain.
    case 'date': {
      const parsed = parseInstant(value);
      if (!parsed) return <span>{toDisplayString(value)}</span>;
      return <span>{parsed.toLocaleDateString()}</span>;
    }

    case 'datetime': {
      const parsed = parseInstant(value);
      if (!parsed) return <span>{toDisplayString(value)}</span>;
      return (
        <span>
          {localeDateIn(parsed, undefined, timeZone)}
          {' '}
          {formatInstantTime(parsed, timeZone)}
        </span>
      );
    }

    case 'text':
      return (
        <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
          {value}
        </p>
      );

    case 'fk':
    case 'one_to_one':
    case 'one_to_many':
      return <span className="badge bg-light text-dark border">{value}</span>;

    case 'integer':
    case 'float':
    case 'decimal':
    case 'numeric':
      if (currencySymbol) {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span style={{ fontWeight: 700, color: '#16a34a' }}>{currencySymbol}</span>
            <span>{value}</span>
          </span>
        );
      }
      return <span>{value}</span>;

    case 'geo_point': {
      if (typeof value === 'object') {
        const lat = value.lat ?? value.latitude ?? '';
        const lng = value.lng ?? value.longitude ?? '';
        return <span>{(lat === '' && lng === '') ? '—' : `${lat}, ${lng}`}</span>;
      }
      return <span>{String(value)}</span>;
    }

    case 'json':
    case 'jsonb': {
      const jsonStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      return (
        <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {jsonStr}
        </span>
      );
    }

    default:
      // Never render an object as "[object Object]" — fall back to a safe string.
      return <span>{toDisplayString(value)}</span>;
  }
}
